import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { createGlobalStyle } from 'styled-components';

interface Crxcr2PopInProps {
  url?: string;
  contentUrl?: string;
  width?: string; // e.g. '75%'
  height?: string; // e.g. '65%'
  timeout?: number | boolean;
  delayClose?: number;
  clickStart?: boolean;
  closeIntent?: boolean;
  postitialBehavior?: boolean;
  closeButtonColor?: string;
  closeCrossColor?: string;
  shadow?: boolean;
  shadowColor?: string;
  shadowOpacity?: string; // e.g. '.5'
  shadeColor?: string;
  shadeOpacity?: string; // e.g. '0'
  border?: string; // e.g. '1px'
  borderColor?: string;
  borderRadius?: string; // e.g. '0px'
  leadOut?: boolean;
  animation?: 'slide' | 'fade' | string;
  direction?: 'up' | 'down' | 'left' | 'right' | string;
  verticalPosition?: 'bottom' | 'top' | 'center' | string;
  horizontalPosition?: 'center' | 'left' | 'right' | string;
  expireDays?: number; // e.g. 0.01
  autoSize?: boolean; // enable responsive auto sizing on mobile
  mobileWidthPercent?: number; // e.g. 92
  mobileHeightPercent?: number; // e.g. 78
  forceFallback?: boolean; // force overlay on (useful for mobile edge-cases)
}

/**
 * Wrapper React pour le PopIn CrackRevenue (crxcr2.com)
 * Injecte la config globale crakPopInParamsIframe puis le script affstitial-min.js
 */
export const Crxcr2PopIn: React.FC<Crxcr2PopInProps> = ({
  url = 'https://t.vlmai-3.com/408074/10046/0?aff_sub5=SF_006OG000004lmDN&aff_sub4=AT_0016',
  contentUrl = 'https://www.gptgirlfriend.online/?aff_id=1&transaction_id=postitial',
  width = '75%',
  height = '65%',
  timeout = false,
  delayClose = 0,
  clickStart = false,
  closeIntent = false,
  postitialBehavior = true,
  closeButtonColor = '#000',
  closeCrossColor = '#fff',
  shadow = true,
  shadowColor = '#000',
  shadowOpacity = '.5',
  shadeColor = '#111',
  shadeOpacity = '0',
  border = '1px',
  borderColor = '#000',
  borderRadius = '0px',
  leadOut = true,
  animation = 'slide',
  direction = 'up',
  verticalPosition = 'bottom',
  horizontalPosition = 'center',
  expireDays = 0.01,
  autoSize = true,
  mobileWidthPercent = 96,
  mobileHeightPercent = 85,
  forceFallback = false,
}) => {
  const [showFallback, setShowFallback] = useState(false);
  const [viewportKey, setViewportKey] = useState(0);
  const [isClient, setIsClient] = useState(false);

  // Recompute on orientation / resize for better auto-sizing
  useEffect(() => {
    // Mark client-ready for portals
    setIsClient(true);
    const onResize = () => setViewportKey(v => v + 1);
    try {
      window.addEventListener('resize', onResize);
      window.addEventListener('orientationchange', onResize as any);
    } catch {}
    return () => {
      try { window.removeEventListener('resize', onResize); } catch {}
      try { window.removeEventListener('orientationchange', onResize as any); } catch {}
    };
  }, []);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const isProd = process.env.NODE_ENV === 'production';
    const isMobileViewport = (() => {
      try { return !!window.matchMedia && window.matchMedia('(max-width: 768px)').matches; } catch { return window.innerWidth <= 768; }
    })();
    const isMobileUA = (() => {
      try { return /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent); } catch { return false; }
    })();
    const isMobile = isMobileViewport || isMobileUA;
    // Mobile: allow vendor library; fallback overlay only via diagnostics later
    // Allow AdSafetyGuard to skip hiding vendor full-screen overlays while this popin is active
    try { (window as any).__adSafetyAllowOverlays = true; } catch {}
    const useWidth = ((): string | number => {
      if (autoSize && isMobileViewport) {
        try {
          const vw = Math.max(320, Math.min(window.innerWidth, 720));
          return Math.round(vw * (Math.min(98, Math.max(88, mobileWidthPercent)) / 100));
        } catch {
          return `${Math.min(98, Math.max(88, mobileWidthPercent))}%`;
        }
      }
      return width;
    })();
    const useHeight = ((): string | number => {
      if (autoSize && isMobileViewport) {
        try {
          const vh = Math.max(400, Math.min(window.innerHeight, 1200));
          return Math.round(vh * (Math.min(92, Math.max(60, mobileHeightPercent)) / 100));
        } catch {
          return `${Math.min(92, Math.max(60, mobileHeightPercent))}%`;
        }
      }
      return height;
    })();
    const effectiveVerticalPosition = ((): string => {
      return autoSize && isMobileViewport ? 'center' : verticalPosition;
    })();
    const effectiveExpireDays = isProd ? expireDays : 0; // en dev, éviter le cookie qui masque le popin
    const effectiveTimeout = isProd
      ? (isMobileViewport ? (typeof timeout === 'number' ? timeout : 3000) : timeout)
      : (typeof timeout === 'number' ? timeout : 3000); // en dev, forcer un déclenchement auto à 3s si false
    const effectivePostitial = isProd
      ? (isMobileViewport ? false : postitialBehavior)
      : false; // en dev, forcer un rendu immédiat

    console.debug('[Crxcr2PopIn] init', { isProd, isMobileViewport, isMobileUA, url, contentUrl, width: useWidth, height: useHeight, effectiveExpireDays, effectiveTimeout, effectivePostitial });
    // 1) Injecter le script de configuration (variable globale requise par la lib)
    const configScript = document.createElement('script');
    configScript.type = 'text/javascript';
    configScript.setAttribute('data-crxcr2', 'config');
    configScript.innerHTML = `
      var crakPopInParamsIframe = {
        url: ${JSON.stringify(url)},
        decryptUrl: false,
        contentUrl: ${JSON.stringify(contentUrl)},
        decryptContentUrl: false,
        contentType: 'iframe',
        width: ${JSON.stringify(useWidth)},
        height: ${JSON.stringify(useHeight)},
        timeout: ${JSON.stringify(effectiveTimeout)},
        delayClose: ${JSON.stringify(delayClose)},
        clickStart: ${JSON.stringify(clickStart)},
        closeIntent: ${JSON.stringify(closeIntent)},
        postitialBehavior: ${JSON.stringify(effectivePostitial)},
        closeButtonColor: ${JSON.stringify(closeButtonColor)},
        closeCrossColor: ${JSON.stringify(closeCrossColor)},
        shadow: ${JSON.stringify(shadow)},
        shadowColor: ${JSON.stringify(shadowColor)},
        shadowOpacity: ${JSON.stringify(shadowOpacity)},
        shadeColor: ${JSON.stringify(shadeColor)},
        shadeOpacity: ${JSON.stringify(shadeOpacity)},
        border: ${JSON.stringify(border)},
        borderColor: ${JSON.stringify(borderColor)},
        borderRadius: ${JSON.stringify(autoSize && isMobileViewport ? '12px' : borderRadius)},
        leadOut: ${JSON.stringify(leadOut)},
        animation: ${JSON.stringify(animation)},
        direction: ${JSON.stringify(direction)},
        verticalPosition: ${JSON.stringify(effectiveVerticalPosition)},
        horizontalPosition: ${JSON.stringify(horizontalPosition)},
        expireDays: ${JSON.stringify(effectiveExpireDays)}
      };
      try { window.crakPopInParamsIframe = crakPopInParamsIframe; } catch (e) {}
    `;
    document.head.appendChild(configScript);

    // 2) Injecter (ou réutiliser) le script de la librairie, en forçant un rechargement pour relire la config globale
    let createdLib = false;
    const existing = document.querySelector('script[data-crxcr2="lib"]') as HTMLScriptElement | null;
    let libScript: HTMLScriptElement | null = null;
    if (existing) {
      try { existing.parentNode?.removeChild(existing); } catch {}
    }
    libScript = document.createElement('script');
    createdLib = true;
    libScript.src = `https://crxcr2.com/popin/latest/affstitial-min.js?v=${Date.now()}`;
    libScript.async = true;
    libScript.setAttribute('data-crxcr2', 'lib');
    libScript.onload = () => {
      console.info('[Crxcr2PopIn] library loaded');
      try {
        // Réassigner la config globale après chargement de la lib (ordre du snippet vendor)
        (window as any).crakPopInParamsIframe = {
          url: url,
          decryptUrl: false,
          contentUrl: contentUrl,
          decryptContentUrl: false,
          contentType: 'iframe',
          width: useWidth,
          height: useHeight,
          timeout: effectiveTimeout as any,
          delayClose: delayClose,
          clickStart: clickStart,
          closeIntent: closeIntent,
          postitialBehavior: effectivePostitial,
          closeButtonColor: closeButtonColor,
          closeCrossColor: closeCrossColor,
          shadow: shadow,
          shadowColor: shadowColor,
          shadowOpacity: shadowOpacity,
          shadeColor: shadeColor,
          shadeOpacity: shadeOpacity,
          border: border,
          borderColor: borderColor,
          borderRadius: (autoSize && isMobileViewport) ? '12px' : borderRadius,
          leadOut: leadOut,
          animation: animation,
          direction: direction,
          verticalPosition: effectiveVerticalPosition,
          horizontalPosition: horizontalPosition,
          expireDays: effectiveExpireDays,
        };
      } catch {}
      try {
        const postCfg = document.createElement('script');
        postCfg.type = 'text/javascript';
        postCfg.setAttribute('data-crxcr2', 'config-post');
        postCfg.text = 'try{window.crakPopInParamsIframe=window.crakPopInParamsIframe;}catch(e){}';
        document.body.appendChild(postCfg);
      } catch {}
    };
    libScript.onerror = (e) => {
      console.error('[Crxcr2PopIn] library failed to load', e);
    };
    (document.body || document.head).appendChild(libScript);
    // Après chargement, essayer de déclencher les inits basées sur des événements globaux
    libScript.addEventListener('load', () => {
      try { window.dispatchEvent(new Event('load')); } catch {}
      try { document.dispatchEvent(new Event('DOMContentLoaded')); } catch {}
    });


    // 3) Diagnostic: après 5s, vérifier si l'iframe de contenu est présente
    const diagTimer = window.setTimeout(() => {
      try {
        const ifr = Array.from(document.querySelectorAll('iframe'))
          .find((el) => (el as HTMLIFrameElement).src?.includes('gptgirlfriend.online')) as HTMLIFrameElement | undefined;
        if (ifr) {
          console.info('[Crxcr2PopIn] content iframe detected');
        } else {
          console.warn('[Crxcr2PopIn] no content iframe detected after 5s; check adblock/CSP/X-Frame-Options');
          // Forcer le fallback si forceFallback est activé (mobile) ou si desktop et script n'a pas chargé
          if (forceFallback || !isMobile) {
            setShowFallback(true);
          }
        }
      } catch {}
    }, 5000);

    return () => {
      try { configScript.remove(); } catch {}
      // On ne supprime pas la lib globale si déjà présente
      if (createdLib && libScript && libScript.parentNode) {
        // Optionnel: laisser en place pour d'autres pages; on peut la retirer si besoin
        // libScript.parentNode.removeChild(libScript);
      }
      try { window.clearTimeout(diagTimer); } catch {}
      // Restore overlay guard flag
      try { (window as any).__adSafetyAllowOverlays = false; } catch {}
    };
  }, [
    url,
    contentUrl,
    width,
    height,
    timeout,
    delayClose,
    clickStart,
    closeIntent,
    postitialBehavior,
    closeButtonColor,
    closeCrossColor,
    shadow,
    shadowColor,
    shadowOpacity,
    shadeColor,
    shadeOpacity,
    border,
    borderColor,
    borderRadius,
    leadOut,
    animation,
    direction,
    verticalPosition,
    horizontalPosition,
    expireDays,
    autoSize,
    mobileWidthPercent,
    mobileHeightPercent,
    viewportKey,
    forceFallback,
  ]);

  // Moved out of the vendor-init effect: prevent background scroll when fallback overlay is visible
  useEffect(() => {
    if (!isClient) return;
    if (!showFallback) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    try { document.documentElement.classList.add('crxcr2-overlay-open'); } catch {}
    return () => { document.body.style.overflow = prev; };
  }, [showFallback, isClient]);

  // Moved out: cleanup html class on unmount
  useEffect(() => {
    if (!isClient) return;
    return () => {
      try { document.documentElement.classList.remove('crxcr2-overlay-open'); } catch {}
    };
  }, [isClient]);

  if (!(isClient && showFallback)) return null;
  return createPortal(
    <>
    <OverlayGlobalStyles />
    <FallbackBackdrop
      id="crxcr2-fallback-backdrop"
      data-safe-overlay="1"
      data-owner="react-portal"
      className="crxcr2-fallback"
      $shadeColor={shadeColor}
      $shadeOpacity={shadeOpacity}
      onClick={() => setShowFallback(false)}
    >
      <FallbackBox
        $width={'100vw'}
        $height={'100dvh'}
        data-safe-overlay="1"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Pop-in partenaire"
      >
        <FallbackClose onClick={() => setShowFallback(false)} aria-label="Fermer">×</FallbackClose>
        <FallbackActionBar>
          <FallbackLink href={contentUrl} target="_blank" rel="nofollow sponsored noopener noreferrer">Ouvrir dans un onglet</FallbackLink>
        </FallbackActionBar>
        <iframe
          src={contentUrl}
          title="Partenaire"
          style={{ 
            position: 'relative',
            width: '100%', 
            height: '100%',
            flex: '1',
            border: '0', 
            display: 'block',
            backgroundColor: '#fff'
          }}
          scrolling="yes"
          loading="eager"
          allowFullScreen
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        />
      </FallbackBox>
    </FallbackBackdrop>
    </>,
    document.body
  );
};

export default Crxcr2PopIn;

// Styles fallback
const OverlayGlobalStyles = createGlobalStyle`
  html.crxcr2-overlay-open #jerky-im-avatar-bubble,
  html.crxcr2-overlay-open .jerky-im-avatar {
    display: none !important;
  }
  html.crxcr2-overlay-open [id^="hp-banner-"],
  html.crxcr2-overlay-open .highperformance-banner,
  html.crxcr2-overlay-open .hilltop-ad,
  html.crxcr2-overlay-open [data-vendor="crxcr2-cams-widget"] {
    display: none !important;
  }
`;
const FallbackBackdrop = styled.div<{ $shadeColor: string; $shadeOpacity: string }>`
  position: fixed;
  inset: 0;
  width: 100vw;
  height: 100dvh;
  background: ${({ $shadeColor, $shadeOpacity }) => `${$shadeColor}${''}`};
  background: rgba(0,0,0, ${props => parseFloat(props.$shadeOpacity || '0.5')});
  display: flex;
  align-items: stretch;
  justify-content: stretch;
  z-index: 2147483647 !important; /* ensure topmost */
`;

const FallbackBox = styled.div<{ $width: string; $height: string }>`
  position: fixed;
  inset: 0;
  width: 100vw;
  height: 100dvh;
  max-width: 100vw;
  max-height: 100dvh;
  background: #fff;
  border: 0;
  border-radius: 0;
  box-shadow: none;
  display: flex;
  flex-direction: column;
  overflow: visible;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
`;

const FallbackClose = styled.button`
  position: absolute;
  top: 6px;
  right: 8px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 2px solid #fff;
  background: #000;
  color: #fff;
  font-size: 22px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 999999;
`;

const FallbackActionBar = styled.div`
  position: absolute;
  bottom: 8px;
  right: 8px;
  left: 8px;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  z-index: 999999;
`;

const FallbackLink = styled.a`
  background: #ffd700;
  color: #000;
  font-weight: 700;
  padding: 6px 10px;
  border-radius: 6px;
  text-decoration: none;
  border: 1px solid #000;
`;
